import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SPANavSlot } from '../../shared/SPANav';
import { useSPAUser } from '../../shared/SPAUser';
import ProgramTree from '../../shared/ProgramTree';
import { useProgramSwitcher } from '../../shared/ProgramTree/usePrograms';

// AIDEV-NOTE: The unified nav's program drill: the shared ProgramTree with the ▾ switcher in the
// drill header for lateral program moves. The program's pages are the lessons page's tab row
// (NodeTabs), not the rail's. Renders nothing when the unified nav is off (SPANavSlot no-ops).

interface ProgramNavRailProps {
  isLoadingTitle: boolean;
  programColor: string | null;
  programId: number;
  programTitle: string;
  // The lesson the page sits inside (the lesson guides page): its tree row reads as current.
  lessonId?: number;
  // The unit the page sits inside (the edit unit page): its tree row opens and reads as current.
  unitId?: number;
}

const ProgramNavRail = ({
  isLoadingTitle, lessonId, programColor, programId, programTitle, unitId,
}: ProgramNavRailProps) => {
  const navigate = useNavigate();
  const { showSearch } = useSPAUser();
  const onSelect = useCallback(
    (value: string) => navigate(`/content/programs/${value}`),
    [navigate],
  );

  const switcher = useProgramSwitcher(programId, onSelect);

  const title = useMemo(() => (isLoadingTitle ? (
    <>
      <span
        aria-hidden="true"
        className="tw-inline-block tw-h-4 tw-w-44 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton"
      />
      <span className="tw-sr-only">Loading program</span>
    </>
  ) : programTitle || `Program ${programId}`), [isLoadingTitle, programId, programTitle]);

  const header = useMemo(
    () => ({
      accentColor: programColor,
      backLabel: 'Content',
      backTo: '/content',
      switcher,
      title,
    }),
    [programColor, switcher, title],
  );

  return (
    <SPANavSlot header={header}>
      <ProgramTree lessonId={lessonId} programId={programId} showSearch={showSearch} unitId={unitId} />
    </SPANavSlot>
  );
};

export default ProgramNavRail;
